import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Mission,
  StreakResult,
  UpdateMissionProgressResult,
  UserMission,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

const MISSION_FIELDS = gql`
  fragment MissionFields on Mission {
    id
    key
    name
    description
    type
    xpReward
    requirement
    isActive
  }
`;

const USER_MISSION_FIELDS = gql`
  ${MISSION_FIELDS}
  fragment UserMissionFields on UserMission {
    id
    userId
    missionId
    progress
    isCompleted
    assignedAt
    completedAt
    mission {
      ...MissionFields
    }
  }
`;

export const getMyActiveMissions = async (user: UserWithToken) =>
  getGraphQLQueryClient(user).request<WithData<UserMission[]>>(
    gql`
      ${USER_MISSION_FIELDS}
      query myActiveMissions {
        data: myActiveMissions {
          ...UserMissionFields
        }
      }
    `
  );

export const getMyMissionHistory = async (
  user: UserWithToken,
  limit?: number
) =>
  getGraphQLQueryClient(user).request<WithData<UserMission[]>>(
    gql`
      ${USER_MISSION_FIELDS}
      query myMissionHistory($limit: Int) {
        data: myMissionHistory(limit: $limit) {
          ...UserMissionFields
        }
      }
    `,
    { limit: limit ?? null }
  );

export type MissionTrackingKey =
  | 'login'
  | 'collection_add'
  | 'marketplace_visit'
  | 'marketplace_list'
  | 'marketplace_sale'
  | 'ranking_view'
  | 'calendar_view'
  | 'tournament_share'
  | 'tournament_participation'
  | 'tournament_match_win'
  | 'daily_missions_completed';

export const trackMissionEvent = async (
  user: UserWithToken,
  trackingKey: MissionTrackingKey,
  amount?: number
) =>
  getGraphQLQueryClient(user).request<WithData<UpdateMissionProgressResult>>(
    gql`
      mutation trackMissionEvent($trackingKey: String!, $amount: Int) {
        data: trackMissionEvent(trackingKey: $trackingKey, amount: $amount) {
          updated {
            userMissionId
            missionKey
            progress
            target
            completed
            xpAwarded
          }
        }
      }
    `,
    { trackingKey, amount: amount ?? 1 }
  );

export const completeMission = async (
  user: UserWithToken,
  userMissionId: number
) =>
  getGraphQLQueryClient(user).request<WithData<UserMission>>(
    gql`
      ${USER_MISSION_FIELDS}
      mutation completeMission($userMissionId: Int!) {
        data: completeMission(userMissionId: $userMissionId) {
          ...UserMissionFields
        }
      }
    `,
    { userMissionId }
  );

export const bumpLoginStreak = async (user: UserWithToken) =>
  getGraphQLQueryClient(user).request<WithData<StreakResult>>(
    gql`
      mutation bumpLoginStreak {
        data: bumpLoginStreak {
          currentStreak
          longestStreak
          streakBumped
          bonusXP
        }
      }
    `
  );

// re-export the Mission type for consumers
export type { Mission };
